import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const applySession = (newToken, userData, setToken, setUser) => {
  localStorage.setItem('token', newToken);
  setToken(newToken);
  axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  setUser(userData);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      setToken(null);
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const data = response.data;

    if (data.mfa_required || data.mfa_setup_required) {
      return data;
    }

    applySession(data.token, data.user, setToken, setUser);
    return data;
  };

  const verifyMfa = async (mfaToken, code) => {
    const response = await axios.post(`${API}/auth/mfa/verify`, {
      mfa_token: mfaToken,
      code,
    });
    applySession(response.data.token, response.data.user, setToken, setUser);
    return response.data;
  };

  const setupMfa = async (mfaToken) => {
    const response = await axios.post(`${API}/auth/mfa/setup`, { mfa_token: mfaToken });
    return response.data;
  };

  const enableMfa = async (mfaToken, secret, code) => {
    const response = await axios.post(`${API}/auth/mfa/enable`, {
      mfa_token: mfaToken,
      secret,
      code,
    });
    applySession(response.data.token, response.data.user, setToken, setUser);
    return response.data;
  };

  const register = async (email, password, firstName, lastName) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      first_name: firstName,
      last_name: lastName,
    });
    applySession(response.data.token, response.data.user, setToken, setUser);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        verifyMfa,
        setupMfa,
        enableMfa,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
